import logWrapper from '../../../../../share/logWrapper';
import { WORKER_EVENT, divideBySystem } from "../../../dataMonitor/dataMonitorUtil";
import DmDataPointConversion from "./dmDataPointConversion";
import apiService from '../../../../../share/util/api/service';
import util from '../../../../../share/util/utils';

/**
 * WorkSupport146用 クラス
 */
export default class WorkSupport146Worker {
  // 停止フラグ
  hasStopOrder = false;
  // データリスト
  dataQueue = [];
  // 送信中フラグ
  isSending = false;
  // APIの共通パス
  baseUrl = '';
  // 変換処理クラスのインスタンス
  dataPointConversion = null;
  // APIから取得したデータポイント(取得ファイル)の総数
  originDataPointsCount = 0;
  // HTTPリクエストのタイムアウト用のタイマー
  timeoutTimer = null;
  // システムごとの選択項目のIDの配列を持つ
  itemIdsBySystem = {};
  // システムごとの各項目の最後のデータを持つ
  lastItems = {};
  // システムごとの最後のタイムスタンプを持つ
  lastTimestamp = {};
  // イベント受信用のコールバック関数
  listenerCallback = null;

  constructor(listenerCallback) {
    this.listenerCallback = listenerCallback;
    this.dataPointConversion = new DmDataPointConversion();
  }

  /**
   * メイン処理
   * @param {string} projectId プロジェクトID
   * @param {string} measuredId 計測ID
   * @param {string} s3path s3path
   * @param {number} totalNumberOfFiles グラフデータの総ファイル数
   * @param {number} startTime グラフデータの計測開始時間
   * @param {array} conversionKeys 変換情報のキー配列
   * @param {array} conversionValues 変換情報の値の配列
   * @param {object} didConversionMap 変換情報のDIDごとのインデックスを持つオブジェクト
   * @param {object} didSizes DIDごとのデータサイズ
   * @param {string} systemId システム名
   * @param {string} cachedProjectDataSource 計測データの取得元の環境(マスター/ミラー)
   */
  async start(projectId, measuredId, s3path, totalNumberOfFiles, startTime, conversionKeys, conversionValues, didConversionMap, didSizes, systemId, cachedProjectDataSource) {
    logWrapper.log('WorkSupport146Worker start');
    this.itemIdsBySystem = divideBySystem(conversionKeys);

    // マーカー情報を取得する
    const params = { region_info: cachedProjectDataSource };
    const markersResponse = await apiService.getParamCall(
      apiService.WSP,
      `/projects/${projectId}/${measuredId}/markers`,
      params
    );

    let hasLastEvaluatedKey = util.hasLastEvaluatedKey(markersResponse?.data);
    while (hasLastEvaluatedKey) {
      // 次のデータが存在するならループを継続、存在しないならページング処理を終了
      const response = await apiService.getParamCall(
        apiService.WSP,
        `/projects/${projectId}/${measuredId}/markers`,
        params
      );
      if (response.data.marker_data.length > 0) {
        markersResponse.data.marker_data = markersResponse.data.marker_data.concat(response?.data?.marker_data);
      }

      hasLastEvaluatedKey = util.hasLastEvaluatedKey(response?.data);
    }
    // Marker情報を表示用に書き換える
    const markerInfo = markersResponse?.data?.marker_data.map(info => {
      return {
        uuid: info.marker_id,
        detail: {
          occurred_elapsed_time: Math.floor(info.marker_time * 1000) // マイクロ秒
        },
        description: info.comment,
        tag: { userId: info.user_id, shared: String(info.is_shared) }
      };
    }) || [];

    // 計測データを取得
    try {
      let sequence_id = 0;
      let firstTimestamp = null;
      // グラフデータ取得APIで返却されるsequence_idがnullになるまでループする
      while (sequence_id !== null) {
        if (this.hasStopOrder) {
          break;
        }

        const graphQueryParam = { region_info: cachedProjectDataSource, s3path: s3path, sequence_id: sequence_id };

        // グラフデータ取得
        const graphDataResponse = await apiService.getParamCall(
          apiService.WSP,
          `/projects/${projectId}/${measuredId}/graphData`,
          graphQueryParam
        );

        const originDataPoints = graphDataResponse?.data?.result_list;
        // 初回のみ通る処理。start_timeをbaseTimeとし、一番初めに取得したdataPointsのlast_timestampをfirstTimestampとする。
        if (sequence_id === 0 && originDataPoints.length > 0) {
          // 先頭のlast_timestampを保持しておく
          firstTimestamp = Number(originDataPoints[0].last_timestamp);
          // グラフ描画の基準が秒のため、基準時刻のミリ秒以下を0になるように調整。調整した時刻は別途親コンポーネントに返す。
          // HACK: #16710対応以前のデータはstartTimeが0なので、計測IDを仮設定する
          const originBaseTime = startTime || Number(measuredId);
          const offset = originBaseTime % 1000;
          this.baseTime = originBaseTime - offset;
          this.listenerCallback({
            data: {
              type: WORKER_EVENT.MEASUREMENT_INFO, measurement: {
                markers: markerInfo,
                basetime: this.baseTime,
                offset: offset,
                received_data_points: totalNumberOfFiles // 総ファイル数
              }
            }
          });
        }
        const dataPoints = originDataPoints.map(dataPoint => {
          // 基準時間のstartTimeにlast_timestampを足し、firstTimestampを引いた時間が計測時間となる。
          const date = new Date((startTime || Number(measuredId)) + Number(dataPoint.last_timestamp) - firstTimestamp);
          const rfc3339String = date.toISOString();
          return { ...dataPoint, data: dataPoint.data, time: rfc3339String, date: date };
        });
        // last_sequence_idは次に取得するファイルに紐づくsequence_id。
        // ファイル名のsequence_idは0から始まるので、last_sequence_idの値がそのまま今までに取得したファイル数となる
        // nullの場合は最終まで取得したので、総ファイル数とする
        const nextSequenceId = graphDataResponse?.data?.last_sequence_id || totalNumberOfFiles;
        this.originDataPointsCount = Number(nextSequenceId);

        sequence_id = graphDataResponse?.data?.last_sequence_id;

        // データポイントの解析
        this.dataQueue = this.createData(dataPoints, didSizes, conversionKeys, conversionValues, didConversionMap, systemId);
        this.startProcessing();
      }
      this.listenerCallback({ data: { type: WORKER_EVENT.SUCCESS_END } });
    } catch (error) {
      logWrapper.log('Get data point error: ', error);
      this.listenerCallback({ data: { type: WORKER_EVENT.RUNTIME_ERROR } });
    } finally {
      this.dataQueue = [];
    }
  }

  /**
   * 最終データの初期化
   * @param {string} systemName システム名
   */
  initLastItems(systemName) {
    if (!this.lastItems[systemName]) {
      this.lastItems[systemName] = {};
      this.lastTimestamp[systemName] = 0;
    }
  }

  /**
   * APIから取得したデータポイントから、親コンポーネントに返すデータを作成する
   * @param {array} dataPoints データポイントの配列
   * @param {object} didSizes DIDごとのデータサイズ
   * @param {array} conversionKeys 変換情報のキー配列
   * @param {array} conversionValues 変換情報の値の配列
   * @param {object} didConversionMap 変換情報のDIDごとのインデックスを持つオブジェクト
   * @param {object} systemId システム名
   * @returns {array} 親コンポーネントに返すデータ
   */
  createData(dataPoints, didSizes, conversionKeys, conversionValues, didConversionMap, systemId) {
    const resultData = [];

    // 現行フォーマットのデータを変換
    for (const dataPoint of dataPoints) {
      const systemName = systemId;
      this.initLastItems(systemName);

      // base64エンコードされた文字列で返されるため、デコードしてUint8Arrayに変換
      const binaryData = this.base64ToUint8Array(dataPoint.data);
      // 1 DIDごとにペイロードを取り出す
      for (const didPayload of DmDataPointConversion.GetDidPayload(binaryData, didSizes, systemName)) {
        const hex_string = Array.from(didPayload.value, byte => byte.toString(16).padStart(2, '0')).join('');
        // 基準時刻と経過時間から計測日時に変換
        const time = dataPoint.date.getTime();
        const convertedData = this.convert(
          { time: time, data_id: didPayload.id, data: hex_string }, conversionKeys, conversionValues, didConversionMap
        );

        // 変換後のデータで、システムごとの各項目の最後のデータを更新する
        const resultFillingData = DmDataPointConversion.updateLastData(
          this.lastItems, this.lastTimestamp, convertedData, systemName, this.itemIdsBySystem);
        // 穴埋め値を先に追加
        resultData.push(...resultFillingData, convertedData);
      }
    }
    return resultData;
  }

  /**
   * データの変換処理を行う
   * @param {object} dataPointModel データポイント
   * @param {array} conversionKeys 変換情報のキー配列
   * @param {array} conversionValues 変換情報の値の配列
   * @param {object} didConversionMap 変換情報のDIDごとのインデックスを持つオブジェクト
   */
  convert(dataPointModel, conversionKeys, conversionValues, didConversionMap) {
    return this.dataPointConversion.dataConvert(dataPointModel, conversionKeys, conversionValues, didConversionMap);
  }

  /**
   * 停止要求
   */
  stop() {
    try {
      logWrapper.log('WorkSupport146Worker stop');
      this.hasStopOrder = true;

      // 停止指示後はそれ以降のデータが来なくなるため、最後の時間に空の項目があれば直前の値で埋める
      Object.keys(this.lastTimestamp).forEach(systemName => {
        const fillingData = DmDataPointConversion.createFilling(this.lastItems[systemName], this.lastTimestamp[systemName]);
        this.dataQueue.push(...fillingData);
      });
      if (this.dataQueue.length) {
        this.startProcessing();
      }
    } finally {
      // 停止完了を親に通知
      this.listenerCallback({ data: { type: WORKER_EVENT.STOPPED } });
      this.lastItems = {};
      this.lastTimestamp = {};
      this.dataPointConversion.clear();
    }
  }

  /**
   * 親への送信処理
   */
  startProcessing() {
    // 送信中の場合、または0件の場合は処理を抜ける
    if (this.isSending || this.dataQueue.length === 0) {
      return;
    }

    // 送信中
    this.isSending = true;

    // 念のため、timeで昇順ソートする
    const sortedData = this.dataQueue.sort((a, b) => a.time - b.time);

    // 親スレッドに受信データを送信
    this.listenerCallback({
      data: {
        type: WORKER_EVENT.CHUNK_DATA,
        chunkData: sortedData,
        funcValues: this.dataPointConversion.funcValues,
        originTotalCount: this.originDataPointsCount
      }
    });

    // バッファ初期化
    this.dataQueue.splice(0);

    // 非送信中
    this.isSending = false;
  }

  /**
   * Base64文字列をUint8Arrayへ変換する
   * @param {string} base64Str Base64エンコードされた文字列
   * @return {Uint8Array} 変換したUint8Array
   */
  base64ToUint8Array(base64Str) {
    const strData = atob(base64Str);
    return new Uint8Array([...strData].map(str => str.charCodeAt(0)));
  }

  /**
   * base64デコード処理（base64 -> 16進数）
   * @param {string} encodedString base64のデータ配列
   * @returns {string} hexString 16進数のデータ配列
   */
  base64ToHex(encodedString) {
    const decodedString = atob(encodedString);
    let hexString = '';

    for (let i = 0; i < decodedString.length; i++) {
      const hex = decodedString.charCodeAt(i).toString(16);
      hexString += hex.padStart(2, '0');
    }
    return hexString;
  }
}
